// Okres kafelków ekranu Statystyki (przegląd zakładki, 28.09.2026). Czyste funkcje, bez React.
//
// Dawniej okres wynikał z trzech miejsc naraz: pusty miesiąc znaczył „bieżący miesiąc”, strzałka
// roku ustawiała pusty miesiąc (więc po przejściu na 2025 kafelki pokazywały wrzesień 2025, a
// wykres cały rok), a sezon „zima” to były styczeń, luty i grudzień TEGO SAMEGO roku, czyli
// kawałki dwóch różnych sezonów. Tu jest jedna definicja, a serwer dostaje ją jako daty
// (`from`/`to`, koniec wyłączny, patrz Login/backend/lib/statystyki.js).

/** Sezony. Zima roku R to grudzień R-1 + styczeń i luty R (decyzja Piotrka, 28.09.2026). */
export const SEZONY = [
  { value: 'winter', miesiace: [12, 1, 2] },
  { value: 'spring', miesiace: [3, 4, 5] },
  { value: 'summer', miesiace: [6, 7, 8] },
  { value: 'autumn', miesiace: [9, 10, 11] },
];

const dwieCyfry = (n) => String(n).padStart(2, '0');

/** Pierwszy dzień miesiąca jako `RRRR-MM-DD`; miesiąc spoza 1..12 przenosi rok. */
export function poczatekMiesiaca(rok, miesiac) {
  const d = new Date(Date.UTC(rok, miesiac - 1, 1));
  return `${d.getUTCFullYear()}-${dwieCyfry(d.getUTCMonth() + 1)}-01`;
}

/**
 * Okres kafelków. `miesiac` 1..12 = ten miesiąc; `sezon` = sezon kończący się w roku `rok`;
 * bez obu = cały rok. Zwraca daty (`od`, `doWylacznie`, koniec wyłączny).
 */
export function okresKpi({ rok, miesiac, sezon }) {
  const r = Number(rok);
  const m = Number(miesiac);
  const s = SEZONY.find((x) => x.value === sezon);
  if (s) {
    const pierwszy = s.value === 'winter' ? { rok: r - 1, miesiac: 12 } : { rok: r, miesiac: s.miesiace[0] };
    return {
      rodzaj: 'sezon',
      rok: r,
      sezon: s.value,
      miesiace: s.miesiace,
      od: poczatekMiesiaca(pierwszy.rok, pierwszy.miesiac),
      doWylacznie: poczatekMiesiaca(pierwszy.rok, pierwszy.miesiac + 3),
    };
  }
  if (Number.isInteger(m) && m >= 1 && m <= 12) {
    return {
      rodzaj: 'miesiac', rok: r, miesiac: m,
      od: poczatekMiesiaca(r, m), doWylacznie: poczatekMiesiaca(r, m + 1),
    };
  }
  return { rodzaj: 'rok', rok: r, od: poczatekMiesiaca(r, 1), doWylacznie: poczatekMiesiaca(r + 1, 1) };
}

/** Parametry zapytania dla serwera: rok (kafelki „w roku”) i daty okresu. */
export function zapytanieOkresu(okres) {
  const miesiac = okres.rodzaj === 'miesiac' ? `&month=${okres.miesiac}` : '';
  return `year=${okres.rok}${miesiac}&from=${okres.od}&to=${okres.doWylacznie}`;
}

/** Rok sezonu do etykiety: zima 2026 to „2025/26”, pozostałe sezony to po prostu rok. */
export function rokSezonu(sezon, rok) {
  const r = Number(rok);
  return sezon === 'winter' ? `${r - 1}/${String(r).slice(-2)}` : String(r);
}

// --- Porównanie rok do roku (decyzja Piotrka, 04.10.2026) ---
// Zastąpiło porównanie ze średnią miesięczną: średnia mieszała sezon z martwymi miesiącami, a dla
// miesiąca w toku zestawiała kilka dni z pełnymi miesiącami. Daty liczymy na napisach
// `RRRR-MM-DD` w UTC, nigdy przez toISOString na dacie lokalnej (ta między 0:00 a 2:00 czasu
// polskiego dawała wczoraj).

const rozbierz = (data) => String(data).split('-').map(Number);
const naNapis = (r, m, d) => `${String(r).padStart(4, '0')}-${dwieCyfry(m)}-${dwieCyfry(d)}`;
const dniWMiesiacu = (rok, miesiac) => new Date(Date.UTC(rok, miesiac, 0)).getUTCDate();
const liczbaLubNull = (v) => (v == null || v === '' || !Number.isFinite(Number(v)) ? null : Number(v));

/** Dzisiejsza data w strefie przeglądarki jako `RRRR-MM-DD`. */
export function dataLokalna(teraz = new Date()) {
  return naNapis(teraz.getFullYear(), teraz.getMonth() + 1, teraz.getDate());
}

/** Data `RRRR-MM-DD` przesunięta o `dni` dni (rachunek w UTC, więc bez zmiany czasu). */
export function przesunDni(data, dni) {
  const [r, m, d] = rozbierz(data);
  const x = new Date(Date.UTC(r, m - 1, d + dni));
  return naNapis(x.getUTCFullYear(), x.getUTCMonth() + 1, x.getUTCDate());
}

/** Ten sam dzień rok wcześniej; 29 lutego przechodzi na 28 lutego, bo dzień musi istnieć. */
export function rokWczesniej(data) {
  const [r, m, d] = rozbierz(data);
  return naNapis(r - 1, m, Math.min(d, dniWMiesiacu(r - 1, m)));
}

/**
 * Co z czym porównać. Okres zakończony (koniec wyłączny <= dziś): cały okres wobec tego samego
 * okresu rok wcześniej. Okres w toku: od początku do DZIŚ WŁĄCZNIE wobec tego samego odcinka rok
 * wcześniej, bo kafelek niesie też rezerwacje z wyprzedzeniem na dni, które jeszcze nie nadeszły.
 * Okres, który się nie zaczął, nie ma porównania. Zwraca `{ stan, biezacy, poprzedni }`, gdzie
 * `stan` to 'zakonczony' | 'w_toku' | 'przyszly', a odcinki to `{ od, doWylacznie }` albo null.
 */
export function porownanieRokDoRoku(okres, teraz = new Date()) {
  const dzis = dataLokalna(teraz);
  if (dzis < okres.od) return { stan: 'przyszly', biezacy: null, poprzedni: null };
  if (okres.doWylacznie <= dzis) {
    // Granice okresu to pierwsze dni miesięcy, więc przesuwają się o rok wprost: luty 2029 wobec
    // CAŁEGO lutego 2028, razem z 29-tym.
    return {
      stan: 'zakonczony',
      biezacy: { od: okres.od, doWylacznie: okres.doWylacznie },
      poprzedni: { od: rokWczesniej(okres.od), doWylacznie: rokWczesniej(okres.doWylacznie) },
    };
  }
  // W toku przesuwamy DZIŚ, nie koniec wyłączny: 28.02.2028 jako koniec wyłączny 29.02 dałby po
  // przesunięciu 28.02.2027 i zgubił 28 lutego roku porównania.
  return {
    stan: 'w_toku',
    biezacy: { od: okres.od, doWylacznie: przesunDni(dzis, 1) },
    poprzedni: { od: rokWczesniej(okres.od), doWylacznie: przesunDni(rokWczesniej(dzis), 1) },
  };
}

/** Parametry zapytania o sam odcinek (bez `year`, więc da się je odróżnić od zapytania kafelków). */
export function zapytanieOdcinka(odcinek) {
  return `from=${odcinek.od}&to=${odcinek.doWylacznie}`;
}

/** Zmiana w procentach; `null`, gdy rok wcześniej nie ma wartości (bez dzielenia przez zero). */
export function zmianaRokDoRoku(biezaca, poprzednia) {
  const b = liczbaLubNull(biezaca);
  const p = liczbaLubNull(poprzednia);
  if (b == null || p == null || p <= 0) return null;
  return ((b - p) / p) * 100;
}

const naDateUtc = (data) => { const [r, m, d] = rozbierz(data); return new Date(Date.UTC(r, m - 1, d)); };
// Intl łączy daty półpauzą; w napisach panelu ma być zwykły myślnik. Odstępy wokół niego
// ustalamy SAMI, bo zależą od wersji ICU: angielski zakres dni jednego miesiąca ICU 73 (Node 20.9)
// pisze bez spacji, a ICU 78 (Node 20.20 na CI, nowe przeglądarki) ze spacjami wokół półpauzy.
const bezPolpauz = (tekst, myslnik) => String(tekst)
  .replace(/\s*[\u2013\u2014]\s*/g, myslnik)
  .replace(/[\u2009\u202f]/g, ' ');

/**
 * Odcinek słowami w języku panelu, koniec wyłączny: „1-3 września 2025”,
 * „1 grudnia 2024 - 15 stycznia 2025”, „3 September 2025”. Skrót (jeden miesiąc, jeden rok)
 * dobiera Intl; formatujemy w UTC, bo daty są północą UTC. W obrębie jednego miesiąca Intl
 * łączy same dni („1-3”), więc myślnik bez spacji; dłuższy odcinek ma pełne daty, więc ze spacjami.
 */
export function opisZakresuDat(od, doWylacznie, lokal) {
  const fmt = new Intl.DateTimeFormat(lokal, { day: 'numeric', month: 'long', year: 'numeric', timeZone: 'UTC' });
  const ostatni = przesunDni(doWylacznie, -1);
  const a = naDateUtc(od);
  const b = naDateUtc(ostatni);
  const myslnik = od.slice(0, 7) === ostatni.slice(0, 7) ? '-' : ' - ';
  if (typeof fmt.formatRange === 'function') return bezPolpauz(fmt.formatRange(a, b), myslnik);
  return a.getTime() === b.getTime() ? fmt.format(a) : `${fmt.format(a)} - ${fmt.format(b)}`;
}

/**
 * Miesiąc z rokiem do zdania „wobec…”: „września 2025”, „September 2025”. Nazwa miesiąca
 * pochodzi z formatu z dniem, bo tylko w nim polski Intl podaje dopełniacz.
 */
export function opisMiesiaca(data, lokal) {
  const d = naDateUtc(data);
  const miesiac = new Intl.DateTimeFormat(lokal, { day: 'numeric', month: 'long', timeZone: 'UTC' })
    .formatToParts(d)
    .find((czesc) => czesc.type === 'month');
  return `${miesiac ? miesiac.value : d.getUTCMonth() + 1} ${d.getUTCFullYear()}`;
}

/**
 * Sezony wykresu dla roku wykresu: zima = grudzień POPRZEDNIEGO roku + styczeń i luty
 * (decyzja Piotrka, ta sama co w `okresKpi`). `biezace`/`poprzednie` = 12 liczb roku wykresu
 * i roku wcześniej. Zwraca sezony z niezerową sumą, w kolejności `SEZONY`, z udziałem w %.
 */
export function sezonyWykresu(biezace, poprzednie) {
  const b = Array.isArray(biezace) ? biezace : [];
  const p = Array.isArray(poprzednie) ? poprzednie : [];
  const wartosc = (s) => s.miesiace.reduce(
    (suma, m) => suma + (Number(s.value === 'winter' && m === 12 ? p[11] : b[m - 1]) || 0),
    0,
  );
  const sezony = SEZONY.map((s) => ({ value: s.value, suma: wartosc(s) }));
  const razem = sezony.reduce((acc, s) => acc + s.suma, 0);
  return sezony.filter((s) => s.suma > 0).map((s) => ({ ...s, procent: razem > 0 ? (s.suma / razem) * 100 : 0 }));
}

/**
 * Wskaźniki „na aktywnego klienta” i „udział aktywnych” z odpowiedzi `/api/kpis-extended`.
 * Licznik i mianownik dotyczą TYCH SAMYCH klientów: rezerwacje i przychód klientów
 * (`clientRentals`/`clientRevenue`, bez blokad bez klienta i bez zanonimizowanych), a gdy
 * serwer ich jeszcze nie zna, dotychczasowe rezerwacje i przychód okresu. Udział nigdy nie
 * przekracza 100%.
 */
export function wskaznikiKlientow(period, customers) {
  const okresKl = period || {};
  const aktywni = Number(okresKl.customersActive) || 0;
  const wszyscy = Number(customers?.totalCustomers) || 0;
  const rezerwacje = liczbaLubNull(okresKl.clientRentals) ?? (Number(okresKl.rentals) || 0);
  const przychod = liczbaLubNull(okresKl.clientRevenue) ?? (Number(okresKl.revenue) || 0);
  return {
    aktywni,
    wszyscy,
    rezerwacjeNaKlienta: aktywni > 0 ? rezerwacje / aktywni : 0,
    wartoscNaKlienta: aktywni > 0 ? przychod / aktywni : 0,
    udzialAktywnych: wszyscy > 0 ? Math.min(100, (aktywni / wszyscy) * 100) : 0,
  };
}

/**
 * Wycinki pączka „Sprzęt dziś”: w użyciu, dostępne i (gdy serwer je podaje) w serwisie albo
 * nieaktywne. Bez tego trzeciego wycinka sztuki w serwisie wpadały do „Dostępny”.
 * Zwraca tylko wycinki z wartością > 0.
 */
export function wycinkiSprzetu(equipment) {
  const e = equipment || {};
  const wycinki = [
    { klucz: 'inUse', value: Number(e.inUseEquipment) || 0 },
    { klucz: 'available', value: Number(e.availableEquipment) || 0 },
    { klucz: 'outOfService', value: Number(e.outOfServiceEquipment) || 0 },
  ];
  return wycinki.filter((w) => w.value > 0);
}
